/**
 * BIOMETRIC LOCK — a local privacy curtain in front of an already-authenticated session.
 *
 * WHAT THIS IS: Face ID (or the iPhone's own passcode) asked for by iOS, which answers us with
 * nothing but yes or no. It decides whether Arvexis RENDERS, never whether the member is signed in.
 *
 * WHAT THIS IS NOT, deliberately:
 *   - It is NOT authentication. The server session is authoritative. A successful unlock does not
 *     create, refresh, extend or replace a session, and a locked screen never appears in front of
 *     an expired one — the session check runs first and wins.
 *   - It stores NO password, NO token, NO PII and NO biometric identifier. Apple performs the match
 *     on-device; Arvexis receives a boolean. Nothing biometric ever reaches us or our servers.
 *   - It uses NO credential storage. The chosen plugin has no such API, which is the point.
 *
 * HONEST LIMIT: the preference below lives in localStorage, so somebody holding an unlocked phone
 * could clear it. This is a curtain against casual access — a partner picking up the phone — not a
 * defence against a determined attacker with the device in hand. Making it tamper-proof would mean
 * account-level or Keychain storage, and neither is authorized.
 */

const LOCK_ENABLED_KEY = 'arvexis_biometric_lock';
const LAST_ACTIVE_KEY = 'arvexis_last_active_at';
const LAST_UNLOCK_KEY = 'arvexis_last_unlock_at';

// Founder decision: a brief glance at a notification must not cost a Face ID prompt.
export const INACTIVITY_LOCK_MS = 5 * 60 * 1000;

const readNumber = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch (e) { return null; }
};

const writeValue = (key, value) => {
  try { localStorage.setItem(key, String(value)); } catch (e) { /* never worth a crash */ }
};

export const isBiometricLockEnabled = () => {
  try { return localStorage.getItem(LOCK_ENABLED_KEY) === '1'; } catch (e) { return false; }
};

export const setBiometricLockEnabled = (enabled) => {
  writeValue(LOCK_ENABLED_KEY, enabled ? '1' : '0');
  // Turning the lock on should not demand Face ID a second later in the very same session.
  if (enabled) markUnlocked();
};

export const markActive = (now = Date.now()) => writeValue(LAST_ACTIVE_KEY, now);
export const markUnlocked = (now = Date.now()) => {
  writeValue(LAST_UNLOCK_KEY, now);
  writeValue(LAST_ACTIVE_KEY, now);
};

export const lastActiveAt = () => readNumber(LAST_ACTIVE_KEY);
export const lastUnlockAt = () => readNumber(LAST_UNLOCK_KEY);

/** Signing out ends the privacy question entirely; the next member of this device starts clean. */
export const clearLockState = () => {
  try {
    localStorage.removeItem(LAST_ACTIVE_KEY);
    localStorage.removeItem(LAST_UNLOCK_KEY);
  } catch (e) { /* not essential */ }
};

/**
 * Must the member unlock before Arvexis renders?
 *
 * Pure on purpose — every timing rule is decided here and proven in tests, so the gate component
 * only has to obey.
 */
export const requiresUnlock = ({
  enabled,
  coldLaunch = false,
  unlockedThisSession = false,
  lastActive = null,
  now = Date.now(),
  inactivityMs = INACTIVITY_LOCK_MS
} = {}) => {
  if (!enabled) return false;
  // Cold launch always asks, however recently the member was here.
  if (coldLaunch) return true;
  if (!unlockedThisSession) return true;
  // Returning from the background: only after a real absence.
  if (lastActive === null) return true;
  return now - lastActive >= inactivityMs;
};

/**
 * What a failed unlock attempt MEANS. The distinction matters: a cancellation must keep Arvexis
 * locked, while a device with no security at all has to be offered a way in that is not a bypass.
 */
export const classifyUnlockFailure = (errorCode) => {
  const code = String(errorCode || '').toLowerCase();
  if (code.includes('usercancel') || code.includes('cancel') || code.includes('systemcancel')) {
    return 'cancelled';
  }
  // No passcode AND no enrolled biometry: iOS can verify nothing for us.
  if (code.includes('passcodenotset') || code.includes('nodevicecredential')) {
    return 'no-device-security';
  }
  if (code.includes('notenrolled') || code.includes('notavailable') || code.includes('biometryunavailable')) {
    return 'unavailable';
  }
  if (code.includes('lockout')) return 'locked-out';
  return 'failed';
};

/**
 * Ask iOS to verify the member. `allowDeviceCredential` hands the fallback to Apple: if Face ID
 * fails, is unavailable, or attempts are exceeded, iOS presents its own passcode sheet.
 * Arvexis never creates, requests, stores or manages a fallback PIN of its own.
 *
 * The plugin is imported lazily so a browser — where no native layer exists — resolves to
 * 'no-device-security' and shows the recovery choices instead of throwing on load.
 */
export const runLocalUnlock = async ({ reason, fallbackTitle } = {}) => {
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason: reason || 'Unlock Arvexis',
      allowDeviceCredential: true,
      iosFallbackTitle: fallbackTitle || 'Use Passcode',
      cancelTitle: 'Cancel'
    });
    markUnlocked();
    return { ok: true };
  } catch (error) {
    const outcome = classifyUnlockFailure(error?.code ?? error?.message);
    return { ok: false, outcome };
  }
};

/** Whether this device can verify anybody at all — biometry or passcode. */
export const checkLocalAuthAvailability = async () => {
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const info = await BiometricAuth.checkBiometry();
    return {
      available: Boolean(info?.isAvailable || info?.strongBiometryIsAvailable),
      biometryType: info?.biometryType ?? null,
      reason: info?.reason ?? null
    };
  } catch (e) {
    return { available: false, biometryType: null, reason: 'unavailable' };
  }
};
